import * as React from 'react';
import { useEffect, useState } from 'react';
import Account from '../../domain/Account';
import { accountUpdateError, adminChangeEmail, adminDeactivateAccount, adminReactivateAccount, getAllAccounts, impersonate } from "../../gateway/accounts";
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColumns } from "@mui/x-data-grid";
import LoginIcon from '@mui/icons-material/Login';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { UUID } from "../../domain/UUID";
import { useAccount } from "../account/AccountContext";
import { ROUTE_DASHBOARD } from "../../UkloRoutes";
import { useNavigate } from "react-router-dom";

export default function AccountTable() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [deactivateTarget, setDeactivateTarget] = useState<Account | null>(null);
  const [changeEmailTarget, setChangeEmailTarget] = useState<Account | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [changeEmailError, setChangeEmailError] = useState('');
  const { account: currentAccount, invalidate } = useAccount();
  const navigate = useNavigate();

  const refreshAccounts = () => {
    getAllAccounts().then(setAccounts);
  };

  useEffect(() => {
    refreshAccounts();
  }, []);

  if (accounts.length === 0) {
    return <Typography>No accounts.</Typography>;
  }

  const handleImpersonate = (id: UUID<Account>) => () => {
    impersonate(id)
      .then(() => navigate(ROUTE_DASHBOARD))
      .then(() => window.location.reload());
  }

  const handleDeactivate = () => {
    if (!deactivateTarget) return;
    adminDeactivateAccount(deactivateTarget.id).then(() => {
      setDeactivateTarget(null);
      refreshAccounts();
    });
  };

  const handleReactivate = (id: UUID<Account>) => () => {
    adminReactivateAccount(id).then(refreshAccounts);
  };

  const openChangeEmail = (target: Account) => {
    setChangeEmailTarget(target);
    setNewEmail('');
    setChangeEmailError('');
  };

  const handleChangeEmail = () => {
    if (!changeEmailTarget) return;
    adminChangeEmail(changeEmailTarget.id, newEmail.trim())
      .then(() => {
        setChangeEmailTarget(null);
        refreshAccounts();
      })
      .catch(error => {
        const details = accountUpdateError(error)?.details;
        setChangeEmailError(details ? `${details}.` : 'Unable to change email address.');
      });
  };

  const columns: GridColumns<Account> = [
    { headerName: 'Name', flex: 3, field: 'name', valueGetter: params => params.row.name },
    { headerName: 'Email', flex: 2, field: 'email', valueGetter: params => params.row.email },
    {
      headerName: 'Roles',
      flex: 2,
      field: 'roles',
      valueGetter: params => params.row.roles.join(', '),
    },
    {
      headerName: 'Status',
      flex: 1,
      field: 'active',
      renderCell: params => (
        <Chip
          label={params.row.active ? 'Active' : 'Deactivated'}
          color={params.row.active ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const actions = [
          // @ts-ignore
          <GridActionsCellItem
            icon={<Tooltip title="Change email"><AlternateEmailIcon /></Tooltip>}
            label="Change email"
            onClick={() => openChangeEmail(row)}
          />
        ];

        if (row.active) {
          actions.push(
            // @ts-ignore
            <GridActionsCellItem
              icon={<Tooltip title="Log in as user"><LoginIcon /></Tooltip>}
              label="Log in as user"
              onClick={handleImpersonate(id as UUID<Account>)}
            />
          );

          if (currentAccount && id !== currentAccount.id) {
            actions.push(
              // @ts-ignore
              <GridActionsCellItem
                icon={<Tooltip title="Deactivate"><PersonOffIcon /></Tooltip>}
                label="Deactivate"
                onClick={() => setDeactivateTarget(row)}
              />
            );
          }
        } else {
          actions.push(
            // @ts-ignore
            <GridActionsCellItem
              icon={<Tooltip title="Reactivate"><PersonIcon /></Tooltip>}
              label="Reactivate"
              onClick={handleReactivate(id as UUID<Account>)}
            />
          );
        }

        return actions;
      },
    }
  ];

  return (
    <>
      <DataGrid
        rows={accounts}
        columns={columns}
        getRowId={account => account.id}
        density="compact"
        autoHeight
        disableColumnSelector
        disableSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
      />
      <Dialog open={deactivateTarget !== null} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate {deactivateTarget?.name}'s account? Their current season
            marking assignments and centre management links will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button onClick={handleDeactivate} color="error" variant="contained">Deactivate</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={changeEmailTarget !== null} onClose={() => setChangeEmailTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Change Email Address</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Change the email address {changeEmailTarget?.name} uses to log in, currently {changeEmailTarget?.email}.
              This takes effect immediately, and a notice is sent to the old address.
            </DialogContentText>
            {changeEmailError && <Alert severity="error">{changeEmailError}</Alert>}
            <TextField
              label="New email address"
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeEmailTarget(null)}>Cancel</Button>
          <Button onClick={handleChangeEmail} variant="contained" disabled={newEmail.trim().length === 0}>
            Change email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
